import React, { useState } from "react";
import "./SubtasksPage.css";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation, useParams } from "react-router-dom";
// ...existing code...
import CircularProgress from '@mui/material/CircularProgress';
import EmptyState from "../../Components/EmptyState/EmptyState";
import ProjectSetup from "../ProjectSetup/ProjectSetup";
import ConfirmationDialog from "../../Components/Popup/ConfirmationDialog";

import BugIcon from "../../assets/icons/bugIcon.svg";
import OpenPageIcon from "../../assets/icons/openPageIcon.svg";

import {
  setIsBugPage,
  setSelectedProject,
  setSelectedProjectBugs,
  setLoading as setBugLoading,
  setIsBugSummaryModal
} from "../../store/slice/bugSlice.js";
import { getBugsByProjId, getSubtasksByProjectName, getBugsBySubtask, updateSubtaskStatus } from "../../services/homeService";
import { setIsProjectSetup } from "../../store/slice/setupSlice.js";
import { getCookie } from "../../services/utils";
import loginData from '../BugList/userLoginIds.json';

const SubtasksPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { clientId, projectId } = useParams();
  
  const { clientName, projectName, guid } = location.state || {};
  const [value, setValue] = useState(null);
  const [loading, setLoading] = useState(false);
  const [subtasksData, setSubtasksData] = useState([]);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [selectedSubtask, setSelectedSubtask] = useState(null);

  const subtaskHeaders = [
    { header: "S. No.", key: "index" },
    { header: "Subtask Name", key: "key" },
    { header: "Assigned User", key: "assigneeDisplayName" },
    { header: "Rows", key: "summary" },
    { header: "Status", key: "status" },
    { header: "Action", key: "action" },
  ];

  // Status options for dropdown
  const statusOptions = ["Start", "To Do", "In Progress", "Closed", "On Hold"];

  // Get the user's email from cookies and check if they are an approver
  const email = getCookie("email");
  const loggedInUserFirstName = getCookie("firstName")?.toLowerCase();
  const isApprovedUser = loginData.login.includes(email);

  const { isProjectSetup } = useSelector((state) => {
    return state.setupDetails;
  });

  const { loading: bugLoading } = useSelector((state) => {
    return state.bugDetails;
  });

  // API function to fetch subtasks
  const fetchSubtasks = async () => {
    if (!projectName) {
      console.error("Project name is not available");
      return;
    }
    
    try {
      setLoading(true);
      const response = await getSubtasksByProjectName(projectName);
      const email = getCookie("email");
      const isApprovedUser = loginData.login.includes(email);
      let filteredSubtasks = response || [];
      
      // Ensure all subtasks have a status field and update "Done" to "Closed"
      filteredSubtasks = filteredSubtasks.map(subtask => ({
        ...subtask,
        status: subtask.status === 'Done' ? 'Closed' : (subtask.status || 'Start')
      }));
      
      if (!isApprovedUser && loggedInUserFirstName) {
        filteredSubtasks = filteredSubtasks.filter(subtask => {
          if (!subtask.assigneeDisplayName) return false;
          
          // Remove "v-" or "V-" prefix if it exists
          let displayName = subtask.assigneeDisplayName;
          if (displayName.startsWith("v-") || displayName.startsWith("V-")) {
            displayName = displayName.substring(2);
          }

          return displayName.toLowerCase().startsWith(loggedInUserFirstName?.toLowerCase()) || displayName.toLowerCase().endsWith(loggedInUserFirstName?.toLowerCase())
        });
      }
      setSubtasksData(filteredSubtasks);
    } catch (error) {
      console.error("Error fetching subtasks:", error);
      setSubtasksData([]);
    } finally {
      setLoading(false);
    }
  };

  // Fetch subtasks when component mounts
  React.useEffect(() => {
    if (projectId) {
      // Make sure to set isBugSummaryModal to false when navigating to subtasks page
      dispatch(setIsBugSummaryModal(false));
      fetchSubtasks();
    }
  }, [projectId]);

  const getBugsByProjIdApi = async (selectedProjectId) => {
    try {
      dispatch(setBugLoading(true));
      
      console.log("Fetching all bugs for projectId:", selectedProjectId);
      
      // Use the existing API to get all bugs for the project (not user-specific)
      const res = await getBugsByProjId(selectedProjectId);
      
      dispatch(setBugLoading(false));
      if (res && res.length === 0) {
        console.log("No bugs found in this project");
        dispatch(setSelectedProjectBugs([]));
      } else if (res) {
        console.log("Found", res.length, "bugs in the project");
        dispatch(setSelectedProjectBugs(res));
      } else {
        dispatch(setBugLoading(false)); 
      }
    } catch (error) {
      console.error("Error fetching bugs by project:", error.message);
      dispatch(setBugLoading(false));
    }
  };

  const handleBugBtn = async (subtask) => {
    const state = {
      clientName: clientName,
      projectName: projectName,
      guid: guid,
      subtaskName: subtask.key,
      subtaskSummary: subtask.summary,
      subtaskId: subtask.id  // Make sure to include the subtask ID in state
    };
    dispatch(setSelectedProjectBugs([]));
    
    try {
      
      
      // Fetch bugs specifically for this subtask
      const subtaskBugs = await getBugsBySubtask(subtask.id);
      console.log("Found", subtaskBugs?.length || 0, "bugs for subtask:", subtask.key);
      let filteredBugs = subtaskBugs || [];
      
      // Filter bugs based on user permissions if needed
      if (!isApprovedUser && loggedInUserFirstName) {
        const filteredByUser = filteredBugs.filter(bug => 
          bug.createdBy && bug.createdBy.toLowerCase().includes(loggedInUserFirstName)
        );
        // If filtering produces results, use them; otherwise keep all bugs
        if (filteredByUser.length > 0) {
          filteredBugs = filteredByUser;
        }
      }
      
      console.log("Navigating to bugs page with", filteredBugs.length, "filtered bugs");
      dispatch(setSelectedProjectBugs(filteredBugs || []));
      dispatch(setSelectedProject({ id: projectId, name: projectName }));
      dispatch(setIsBugPage(true));
      
      navigate(`/client/${clientId}/project/${projectId}/subtask/${subtask.id}/bugs`, {
        state,
      });
    } catch (error) {
      console.error("Error fetching bugs for subtask:", error);
      dispatch(setSelectedProjectBugs([]));
      dispatch(setSelectedProject({ id: projectId, name: projectName }));
      dispatch(setIsBugPage(true));
      
      navigate(`/client/${clientId}/project/${projectId}/subtask/${subtask.id}/bugs`, {
        state,
      });
    }
  };

  const handleOpenPageBtn = (subtask) => {
    // Check if the assigned user starts with the logged-in user's firstName (case-insensitive)
    if (subtask.assigneeDisplayName && subtask.assigneeDisplayName !== "Unassigned") {
      // Remove "v-" or "V-" prefix if it exists
      let displayName = subtask.assigneeDisplayName;
      if (displayName.startsWith("v-") || displayName.startsWith("V-")) {
        displayName = displayName.substring(2);
      }

      if (!displayName.toLowerCase().startsWith(loggedInUserFirstName) && !displayName.toLowerCase().endsWith(loggedInUserFirstName)) {
        // Show confirmation dialog
        setSelectedSubtask(subtask);
        setShowConfirmDialog(true);
        return;
      }
    }
    // Proceed with navigation if user is assigned or confirmed to continue
    navigateToSubtaskPage(subtask);
  };

  const navigateToSubtaskPage = (subtask) => {
    // Open iframe page in a new tab instead of navigating in the current tab
    const url = `/client/${clientId}/project/${projectId}/subtask/${subtask.id}/iframe`;
    
    // Create URL object to add state parameters as query parameters
    const urlWithParams = new URL(url, window.location.origin);
    urlWithParams.searchParams.append('clientName', clientName || '');
    urlWithParams.searchParams.append('projectName', projectName || '');
    urlWithParams.searchParams.append('guid', guid || '');
    urlWithParams.searchParams.append('subtaskName', subtask.key || '');
    urlWithParams.searchParams.append('subtaskSummary', subtask.summary || '');
    
    // Open in new tab
    window.open(urlWithParams.toString(), '_blank');
  };

  const handleConfirmAccess = () => {
    if (selectedSubtask) {
      navigateToSubtaskPage(selectedSubtask);
      setSelectedSubtask(null);
    }
  };

  const handleCancelAccess = () => {
    setSelectedSubtask(null);
  };

  const handleStatusChange = async (subtask, newStatus) => {
    try {
      // Make API call to update the status on the backend using the subtask key
      await updateSubtaskStatus(subtask.key, newStatus);
      console.log(`Status updated for subtask ${subtask.key} to ${newStatus}`);
      
      // Update local state to reflect the change immediately
      setSubtasksData(prevData => 
        prevData.map(item => 
          item.id === subtask.id 
            ? { ...item, status: newStatus } 
            : item
        )
      );
    } catch (error) {
      console.error("Error updating subtask status:", error);
      // Optionally, show an error notification to the user here
    }
  };

  const handleBackBtn = () => {
    navigate(`/client/${clientId}`, { 
      state: { clientName: clientName } 
    });
  };

  const handleBackPageSetup = () => {
    dispatch(setIsProjectSetup(false));
  };

  const options = subtasksData?.map((subtask) => ({
    title: subtask.key,
  })) || [];

  return isProjectSetup ? (
    <ProjectSetup handleBack={handleBackPageSetup} />
  ) : (
    <>
      {!subtasksData.length ? (
        loading ? (
          <div className="loader">
            <CircularProgress />
          </div>
        ) : (
          <EmptyState
            text={`You don't have any subtasks added for this project.`}
            btnText={`Add Subtask`}
            handleBtn={() => {}}
          />
        )
      ) : (
        <div className="subtasks-wrapper">
          <div className="projectTable">
            <h2 className="tabletitle">Subtasks List</h2>
            {/* Search bar removed as requested */}
          </div>
          <div className="project-table">
            <table>
            <thead>
              <tr>
                {subtaskHeaders.map((header, index) => (
                  <th
                    key={index}
                    style={{
                      textAlign: header.header === "Subtask Name" || header.header === "Rows" ? "left" : "center",
                    }}
                  >
                    {header.header}
                  </th>
                ))}
              </tr>
            </thead>

            <tbody>
              {subtasksData
                .map((subtask, index) => {
                  return (
                    <tr
                      key={index}
                      className={index % 2 === 0 ? "evenRow" : "oddRow"}
                    >
                      <td>{index + 1}</td>
                      <td style={{ textAlign: "left" }}>
                        <div className="subtask-info">
                          <strong>{subtask.key || 'N/A'}</strong>
                        </div>
                      </td>
                      <td>{
                        subtask.assigneeDisplayName ? 
                          (subtask.assigneeDisplayName.startsWith("v-") || subtask.assigneeDisplayName.startsWith("V-") ? 
                            subtask.assigneeDisplayName.substring(2) : 
                            subtask.assigneeDisplayName) : 
                          'Unassigned'
                      }</td>
                      <td style={{ textAlign: "left" }}>{subtask.summary || 'N/A'}</td>
                      <td>
                        <div className={`status-container status-${subtask.status?.replace(/\s+/g, '-').toLowerCase() || 'start'}`}>
                          <span className={`status-indicator status-${subtask.status?.replace(/\s+/g, '-').toLowerCase() || 'start'}`}></span>
                          <span className={`status-text status-${subtask.status?.replace(/\s+/g, '-').toLowerCase() || 'start'}`}>
                            {subtask.status || 'Start'}
                          </span>
                        </div>
                      </td>
                      <td>
                        <div className="action-buttons">
                          {subtask.summary && !subtask.summary.startsWith("LR_") && (
                            <button
                              className="buttonClass"
                              onClick={() => handleOpenPageBtn(subtask)}
                            >
                              Open Pages
                            </button>
                          )}

                          <button
                            className="buttonClass"
                            onClick={() => handleBugBtn(subtask)}
                          >
                            <img
                              src={BugIcon}
                              alt="bugIcon"
                              draggable={false}
                            />
                            Bugs
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </div>
        </div>
      )}
      
      <ConfirmationDialog
        open={showConfirmDialog}
        handleClose={() => setShowConfirmDialog(false)}
        title="Subtask Assignment Confirmation"
        message={`This subtask is assigned to "${
          selectedSubtask?.assigneeDisplayName ? 
            (selectedSubtask.assigneeDisplayName.startsWith("v-") || selectedSubtask.assigneeDisplayName.startsWith("V-") ?
              selectedSubtask.assigneeDisplayName.substring(2) : 
              selectedSubtask.assigneeDisplayName) : 
            'Unassigned'
        }". Do you want to continue?`}
        onConfirm={handleConfirmAccess}
        onCancel={handleCancelAccess}
        confirmText="Continue"
        cancelText="Cancel"
      />
    </>
  );
};

export default SubtasksPage;
